// Keep the cart drawer in sync with external cart mutations (e.g. Appstle
// subscriptions auto-adding a free/gift product). The theme's own pub/sub
// only fires for changes the theme makes; third-party apps post directly to
// /cart/*.js and the drawer never refreshes.
//
// We wrap fetch + XMLHttpRequest, detect mutations to /cart/add|change|update|clear.js,
// and call cart-drawer.reload() after the response settles.
//
// We also strip stray `selling_plan` inputs from non-PDP product forms before
// submission. Appstle injects a selling_plan input on the PDP for the active
// subscription; when other product-forms on the page (quick-add cards, cart
// drawer recommendations) submit, they would otherwise include that selling
// plan and Shopify rejects with HTTP 422 "Cannot apply selling plan to variant"
// because those variants don't have the plan.

(function () {
  function onSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    // Leave the main PDP form alone — that's where subscriptions are intended.
    if (form.closest('product-info, .section-main-product, main-product')) return;
    // Strip any selling_plan inputs from quick-add / recs / drawer forms.
    var inputs = form.querySelectorAll('input[name="selling_plan"]');
    if (inputs.length) {
      inputs.forEach(function (input) { input.parentNode.removeChild(input); });
    }
  }
  // Capture phase so we run before product-form.js builds its FormData.
  document.addEventListener('submit', onSubmit, true);
})();

(function () {
  var CART_MUT_RE = /\/cart\/(add|change|update|clear)(?:\.js|\.json|$)/i;
  // Settle window was previously used to suppress flicker from Appstle re-adds,
  // but it caused checkout-vs-drawer mismatches (drawer showed pre-Appstle
  // snapshot, checkout read actual server state). The cart-drawer's two-pass
  // render now pins free gifts to the bottom regardless of insertion order,
  // so we no longer need to defer — the drawer can always reflect server truth.
  var SETTLE_MS = 0;
  var lastThemeMutation = 0;
  var debounceTimer = null;

  function scheduleRefresh() {
    var sinceTheme = Date.now() - lastThemeMutation;
    // If we're still inside the settling window, push the refresh out until
    // the window closes. Multiple rapid Appstle calls coalesce into one.
    var wait = sinceTheme < SETTLE_MS ? (SETTLE_MS - sinceTheme + 80) : 80;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refreshCart, wait);
  }

  function refreshCart() {
    // Final safety: don't reload mid-render if the theme just acted.
    if (Date.now() - lastThemeMutation < SETTLE_MS) return;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.reload === 'function') {
      try { drawer.reload(); } catch (e) { console.warn('[cart-sync] reload failed', e); }
    }
    try {
      if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-external-sync' });
      }
    } catch (e) { /* no-op */ }
  }

  function isCartMutation(url, method) {
    if (!url) return false;
    if (method && String(method).toUpperCase() !== 'POST') return false;
    try {
      var u = typeof url === 'string' ? url : (url.url || String(url));
      return CART_MUT_RE.test(u);
    } catch (e) { return false; }
  }

  // The theme's own cart.js posts with a "sections" field in the body — when
  // it does, it already re-renders the drawer itself, so we must NOT refresh
  // again or we get a double-render flicker (the visible "free item gets pushed
  // to top" jitter the user reported). External apps like Appstle never set
  // this field, so they still trigger our refresh.
  function bodyMentionsSections(body) {
    if (body == null) return false;
    try {
      if (typeof body === 'string') return body.indexOf('sections') !== -1;
      if (body instanceof FormData) {
        var has = false;
        body.forEach(function (_, k) { if (k === 'sections' || k === 'sections_url') has = true; });
        return has;
      }
      if (body instanceof URLSearchParams) return body.has('sections') || body.has('sections_url');
    } catch (e) { /* fall through */ }
    return false;
  }

  // --- fetch wrapper ---
  if (window.fetch) {
    var origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var body = (init && init.body) || (input && input.body) || null;
      var isMut = isCartMutation(url, method);
      var isTheme = isMut && bodyMentionsSections(body);
      if (isTheme) lastThemeMutation = Date.now();

      // Safety net for the "Cannot apply selling plan to variant" 422 — happens
      // when something (Appstle on this store) leaves a selling_plan input
      // attached to a quick-add form that runs after the gift is in the cart.
      // The submit-time stripper above usually catches it, but if a script
      // re-injects after our handler runs, we still see the 422 here. Retry
      // once without selling_plan and return the retry response, so the caller
      // sees a normal success.
      var isAddWithPlan = isMut &&
        method.toUpperCase && method.toUpperCase() === 'POST' &&
        /\/cart\/add(?:\.js|\.json|$)/.test(url) &&
        body instanceof FormData && body.has('selling_plan');

      var p = origFetch(input, init);

      if (isAddWithPlan) {
        p = p.then(function (res) {
          if (res.status !== 422) return res;
          return res.clone().json().then(function (data) {
            var desc = (data && (data.description || data.message)) || '';
            if (String(desc).toLowerCase().indexOf('selling plan') === -1) return res;
            var newBody = new FormData();
            body.forEach(function (val, key) {
              if (key !== 'selling_plan') newBody.append(key, val);
            });
            return origFetch(input, Object.assign({}, init || {}, { body: newBody }));
          }).catch(function () { return res; });
        });
      }

      if (isMut && !isTheme) {
        p.then(function (res) { if (res && res.ok) scheduleRefresh(); }).catch(function () { /* ignore */ });
      }
      return p;
    };
  }

  // --- XHR wrapper ---
  var XHRopen = XMLHttpRequest.prototype.open;
  var XHRsend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartSync = { method: method, url: url };
    return XHRopen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var info = this.__cartSync;
    if (info && isCartMutation(info.url, info.method)) {
      var isTheme = bodyMentionsSections(body);
      if (isTheme) lastThemeMutation = Date.now();
      if (!isTheme) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) scheduleRefresh();
        });
      }
    }
    return XHRsend.apply(this, arguments);
  };
})();
